import { useEffect, useLayoutEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LoaderCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import authService from "@/services/auth.service";
import Alert from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/auth.context";
import { Link, useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import { useOrganization } from "@/context/organization.context";
import useDomain from "@/hooks/use-domain";
import { useTranslation } from "react-i18next";
import { parseAuthError } from "@/lib/auth-errors";

const formSchema = yup.object().shape({
  email: yup
    .string()
    .email()
    .required("Phone number is required")
    .label("Email"),
  password: yup.string().required("Password is required").label("Passcord"),
});

export default function TbLogin() {
  const { t } = useTranslation("auth");
  const location = useLocation();
  const { activeOrg } = useOrganization();

  const { settings } = useSettings();

  const form = useForm({
    resolver: yupResolver(formSchema),
    values: {
      email: "",
      password: "",
    },
  });

  const redirect = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.redirect;

  const defaultEmail = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.email;

  useEffect(() => {
    if (defaultEmail) {
      form.setValue("email", defaultEmail);
    }
  }, [defaultEmail]);

  const { setCurrentUser } = useAuth();

  const navigate = useNavigate();
  const [error, setError] = useState("");

  const { isAdmin, orgDomain } = useDomain(env_config[app_env].domain);

  const submit = async (values: any) => {
    setError("");
    try {
      setError("");
      const res = await authService.signIn({
        email: values.email,
        password: values.password,
      });
      setCurrentUser(res);
      navigate(redirect || "/redirect");
    } catch (e) {
      const { message, fieldErrors } = parseAuthError(e, t);
      setError(message);
      Object.keys(fieldErrors)?.forEach((field: any) => {
        form.setError(field, {
          message: fieldErrors[field],
        });
      });
    }
  };

  const queryParams = new URLSearchParams(location.search);
  const queryString = queryParams.toString(); // This will give you the query string

  const [mode, setMode] = useState("password");

  useLayoutEffect(() => {
    if (settings?.enable_passcode_auth && orgDomain) {
      setMode("passcode");
    }
  }, [settings?.enable_passcode_auth, orgDomain]);

  return (
    <>
      <div className="min-h-screen flex flex-col">
        <header className="bg-background border-b shadow-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex justify-between items-center">
            <Link to="/" className="flex gap-2 items-center">
              <img className="h-7" src="/logo.svg" alt="" />
              <img className="h-[22px]" src="/logo_b.png" alt="" />
            </Link>
          </div>
        </header>

        <div className="bg-gradient-to-b h-full flex-1 dark:bg-background bg-orange-50/50 flex ivtems-center justify-center px-3 py-4">
          <div className={`h-auto w-full`}>
            {mode === "passcode" && (
              <div className="pt-8">
                <PinLoginForm setMode={setMode} />
              </div>
            )}

            {mode === "password" && (
              <Card className="transition-all mt-4 sm:mt-8 mx-auto max-w-[380px]">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-lg text-center">
                    Welcome back Again.
                  </CardTitle>
                  <CardDescription className="text-center !text-sm">
                    Enter your credentials to access your account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pb-3">
                  <div>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <form onSubmit={form.handleSubmit(submit)}>
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label htmlFor="phone">Email</Label>
                          <Input
                            placeholder="Enter your email"
                            value={form.watch("email")}
                            onChange={(e) =>
                              form.setValue("email", e.target.value)
                            }
                            error={form.formState?.errors["email"]?.message}
                            name="email"
                          />
                        </div>
                        <div className="space-y-1">
                          <div className="flex !mb-2 items-center justify-between">
                            <Label htmlFor="phone">Password</Label>
                            <Link
                              className="text-sm underline"
                              to="/forgot-password"
                            >
                              Forgot Your password?
                            </Link>
                          </div>
                          <Input
                            placeholder="Enter your password"
                            value={form.watch("password")}
                            onChange={(e) =>
                              form.setValue("password", e.target.value)
                            }
                            type="password"
                            error={form.formState?.errors["password"]?.message}
                            name="password"
                          />
                        </div>
                      </div>
                      <Button
                        disabled={form.formState.isSubmitting}
                        onClick={form.handleSubmit(submit)}
                        className="w-full !mt-6"
                        type="submit"
                      >
                        {form.formState.isSubmitting && (
                          <LoaderCircle
                            className="-ms-1 me-2 animate-spin"
                            size={16}
                          />
                        )}
                        Login your account
                      </Button>
                    </form>
                  </div>
                </CardContent>
              </Card>
            )}
            {activeOrg && (
              <div className="text-center text-sm text-gray-500 py-4">
                ** {activeOrg?.name} **
              </div>
            )}
            {isAdmin && (
              <div className="text-center text-sm text-gray-500 py-4">
                ** Admin Portal **
              </div>
            )}
          </div>
        </div>
        <footer className="bg-background py-4 border-t">
          <div className="max-w-6xl flex justify-center sm:justify-between mx-auto px-6 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
            <p>© 2025 Table Tronic. All rights reserved.</p>
            <div className="hidden sm:flex gap-4 items-center">
              <a href="#" className="underline">
                Terms & Conditions
              </a>
              <a href="#" className="underline">
                Privacy Policy
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

import { Delete, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import useSettings from "@/hooks/useSettings";
import { app_env, env_config } from "@/env-config";

export function PinLoginForm({ setMode }) {
  const { t } = useTranslation("auth");
  const [pin, setPin] = useState("");
  const [showPin] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const maxLength = 6;
  // A digit key never changes colour, size or focus ring on hover or press:
  // a key that lights up tells whoever is standing behind the cashier which
  // digits the PIN is made of.
  const digitKeyClass =
    "font-semibold transition-none hover:bg-background hover:border-input hover:text-foreground active:scale-100 active:bg-background focus-visible:ring-0 focus-visible:ring-offset-0";
  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

  const handleNumberClick = (number: number) => {
    if (pin.length < maxLength) {
      setPin((prev) => prev + number.toString());
    }
  };

  const handleDelete = () => {
    setPin((prev) => prev.slice(0, -1));
    setError("");
  };

  const handleClear = () => {
    setPin("");
    setError("");
  };

  const { orgDomain } = useDomain("one-suite.cloud");

  const redirect = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.redirect;

  const { setCurrentUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (pin.length === 0 || isLoading) return;

    setIsLoading(true);
    setError("");

    try {
      // Simulate API call
      const res = await authService.loginWithPasscode({
        passcode: pin,
        domain: orgDomain,
      });
      setPin("");
      setCurrentUser(res);

      // Only billing managers get past a locked subscription (see
      // `assertLoginAccess`), so anyone reaching here with `locked` can pay.
      if (
        (res as { billing?: { state?: string } })?.billing?.state === "locked"
      ) {
        navigate("/billing-required?canPay=1");
        return;
      }

      navigate(redirect || "/redirect");
    } catch (error) {
      const { message } = parseAuthError(error, t);
      setError(message);
      setPin("");
    }

    setIsLoading(false);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const activeElement = document.activeElement as HTMLElement | null;
      const isTypingInField =
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA" ||
        activeElement?.tagName === "SELECT" ||
        activeElement?.isContentEditable;

      if (isTypingInField) {
        return;
      }

      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        handleNumberClick(Number(event.key));
        return;
      }

      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        handleDelete();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        handleClear();
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isLoading, pin.length]);

  // Auto-submit when PIN reaches max length
  useEffect(() => {
    if (pin.length === maxLength) {
      handleSubmit();
    }
  }, [pin]);

  return (
    <Card className="w-[360px] max-w-md mx-auto">
      <CardHeader className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
          <Shield className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-lg font-semibold">Enter Your PIN</CardTitle>
        <CardDescription>
          Please enter your {maxLength}-digit PIN to continue
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* PIN Display */}
        <div className="space-y-4">
          <div className="flex items-center justify-center space-x-2">
            <div className="flex space-x-2">
              {Array.from({ length: maxLength }).map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "w-12 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-mono transition-all duration-200",
                    index < pin.length
                      ? "border-primary bg-primary/5 text-foreground"
                      : "border-border bg-muted/30",
                  )}
                >
                  {index < pin.length && (showPin ? pin[index] : "•")}
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="text-destructive text-sm text-center font-medium animate-shake">
              {error}
            </div>
          )}
        </div>

        {/* Visual Keyboard */}
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {numbers.slice(0, 9).map((number) => (
              <Button
                key={number}
                variant="outline"
                size="lg"
                onClick={() => handleNumberClick(number)}
                disabled={isLoading || pin.length >= maxLength}
                className={cn("h-[50px] text-lg", digitKeyClass)}
                aria-label={`Enter ${number}`}
              >
                {number}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Button
              variant="outline"
              size="lg"
              onClick={handleClear}
              disabled={isLoading || pin.length === 0}
              className="h-14 text-sm font-medium hover:bg-secondary hover:text-secondary-foreground transition-all duration-200"
              aria-label="Clear all"
            >
              Clear
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => handleNumberClick(0)}
              disabled={isLoading || pin.length >= maxLength}
              className={cn("h-14 text-xl", digitKeyClass)}
              aria-label="Enter 0"
            >
              0
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={handleDelete}
              disabled={isLoading || pin.length === 0}
              className="h-14 hover:bg-secondary hover:text-secondary-foreground transition-all duration-200"
              aria-label="Delete last digit"
            >
              <Delete className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div>
          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={isLoading || pin.length === 0}
            className="w-full h-12 text-base"
            aria-label="Submit PIN"
          >
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                <span>Verifying...</span>
              </div>
            ) : (
              "Login Your Account"
            )}
          </Button>
          <p className="text-sm text-center mt-3 text-muted-foreground">
            Don't have a PIN?
            <a
              onClick={() => setMode("password")}
              className="underline text-orange-500 cursor-pointer ml-2"
            >
              Use Password
            </a>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
